import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { statsAPI } from "@/lib/api";
import { 
  Truck, 
  Plane, 
  BookOpen,
  List,
  Plus,
  Loader2
} from "lucide-react";

export default function DashboardOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await statsAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch stats:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black tracking-tight mb-2">
          Welcome, {user?.name}
        </h1>
        <p className="text-muted-foreground">
          {user?.company_name || "Partner Dashboard"}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          <div className="bg-background p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <span className="label-text">Cargo Listings</span>
            </div>
            <p className="text-4xl font-black">{stats?.cargo_listings || 0}</p>
          </div>
          <div className="bg-background p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500 flex items-center justify-center">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <span className="label-text">Jet Listings</span>
            </div>
            <p className="text-4xl font-black">{stats?.jet_listings || 0}</p>
          </div>
          <div className="bg-background p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-500 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="label-text">Total Bookings</span>
            </div>
            <p className="text-4xl font-black">{stats?.total_bookings || 0}</p>
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <Link to="/partner/listings">
          <Button className="rounded-none btn-hover" data-testid="view-listings-btn">
            <List className="w-4 h-4 mr-2" />
            View My Listings
          </Button>
        </Link>
        <Link to="/partner/add">
          <Button variant="outline" className="rounded-none btn-hover" data-testid="add-listing-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add New Listing
          </Button>
        </Link>
      </div>
    </div>
  );
}
